import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Phone, Send, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { t, tx } from '@/translations/translations';

const Contact = () => {
  const { language } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', message: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success(
          tx(language, 'Mensaje enviado!', 'Message sent!', 'ההודעה נשלחה!'),
          { description: tx(language, 'Te responderemos pronto.', 'We will respond soon.', 'נחזור אליכם בקרוב.') }
        );
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error(tx(language, 'Error al enviar', 'Failed to send', 'שגיאה בשליחה'));
      }
    } catch {
      toast.error(tx(language, 'Error de conexión', 'Connection error', 'שגיאת חיבור'));
    } finally {
      setSubmitting(false);
    }
  };

  const contactCards = [
    {
      icon: Mail,
      title: t(language, 'contact.email'),
      value: 'mitzvafit@gmail.com',
      href: 'mailto:mitzvafit@gmail.com',
      color: 'from-cyan-500 to-blue-600',
      bg: 'bg-cyan-500/10',
      iconColor: 'text-cyan-400',
      testId: 'contact-email-link',
    },
    {
      icon: Phone,
      title: 'WhatsApp',
      value: '+52 55 4323 3705',
      href: 'https://wa.me/525543233705?text=%C2%A1Hola%21%20Tengo%20una%20duda%20sobre%20los%20tzitziot%20de%20Mitzva%20Fit.',
      color: 'from-green-500 to-emerald-600',
      bg: 'bg-green-500/10',
      iconColor: 'text-green-400',
      subtitle: t(language, 'contact.phoneHours'),
      testId: 'contact-phone-link',
    },
    {
      icon: () => (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
      ),
      title: 'Instagram',
      value: '@mitzva_fit',
      href: 'https://instagram.com/mitzva_fit',
      color: 'from-purple-500 to-pink-600',
      bg: 'bg-purple-500/10',
      iconColor: 'text-purple-400',
      testId: 'contact-instagram-link',
    },
  ];

  return (
    <section id="contacto" className="py-12 md:py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-slate-900 rounded-full px-5 py-2 mb-6">
            <Mail className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-white text-xs font-bold uppercase tracking-widest">
              {tx(language, 'Contacto', 'Contact', 'צור קשר')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3">
            {t(language, 'contact.title')}
          </h2>
          <p className="text-base text-slate-500 max-w-xl mx-auto">
            {t(language, 'contact.subtitle')}
          </p>
        </div>

        {/* Contact cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {contactCards.map((card, idx) => {
            const Icon = card.icon;
            return (
              <a
                key={idx}
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 p-5 rounded-2xl bg-white border-2 border-slate-100 hover:border-slate-300 hover:shadow-xl transition-all duration-300"
                data-testid={card.testId}
              >
                <div className={`w-12 h-12 rounded-xl ${card.bg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                  <span className={card.iconColor}><Icon /></span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{card.title}</p>
                  <p className="text-slate-800 font-semibold text-sm truncate">{card.value}</p>
                  {card.subtitle && <p className="text-xs text-slate-400 mt-0.5">{card.subtitle}</p>}
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
              </a>
            );
          })}
        </div>

        {/* Form */}
        <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-lg p-8 sm:p-10">
          <div className="mb-8">
            <h3 className="text-xl font-bold text-slate-900 mb-1">{t(language, 'contact.formTitle')}</h3>
            <p className="text-sm text-slate-400">{t(language, 'contact.formDesc')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">{t(language, 'contact.fullName')} *</Label>
                <Input
                  id="name" name="name" value={formData.name} onChange={handleChange} required
                  placeholder={t(language, 'contact.namePlaceholder')}
                  className="rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500/20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">{t(language, 'contact.emailLabel')} *</Label>
                <Input
                  id="email" name="email" type="email" value={formData.email} onChange={handleChange} required
                  placeholder={t(language, 'contact.emailPlaceholder')}
                  className="rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-slate-700">{t(language, 'contact.phoneLabel')}</Label>
              <Input
                id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange}
                placeholder={t(language, 'contact.phonePlaceholder')}
                className="rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500/20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-sm font-medium text-slate-700">{t(language, 'contact.message')} *</Label>
              <Textarea
                id="message" name="message" value={formData.message} onChange={handleChange} required rows={5}
                placeholder={t(language, 'contact.messagePlaceholder')}
                className="rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500/20 resize-none"
              />
            </div>

            <Button
              type="submit" disabled={submitting}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-6 rounded-xl text-base transition-all"
              data-testid="contact-submit-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting
                ? tx(language, 'Enviando...', 'Sending...', 'שולח...')
                : t(language, 'contact.btnSend')}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
